import { Button, Divider } from "@mui/material";
import { ArrowRightIcon, CheckCircleIcon } from "@phosphor-icons/react";
import { useRouter } from "@tanstack/react-router";
import { useCallback, useEffect } from "react";
import { useTranslations } from "use-intl";

import { useFeatureIsOn } from "@/app/featureFlags";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { useSelectedDepartureFlight } from "@/features/flights/hooks/flightSearch/useSelectedDepartureFlight";
import { useSelectedOffer } from "@/features/flights/hooks/flightSearch/useSelectedOffer";
import { calculateMonthlyInstallment, includeBnplFee } from "@/utils/bnpl-fee";
import { formatCurrency } from "@/utils/currency";

export const SelectedDepartureFlightDrawer = () => {
  const t = useTranslations();
  const { history } = useRouter();
  const { result: selectedDepartureFlight } = useSelectedDepartureFlight();
  const { result: selectedOffer } = useSelectedOffer();
  const { maxAvailableInstallments, isEligible } = usePaymentContext();
  const isBnplFeatureEnabled = useFeatureIsOn("bnpl") && Boolean(isEligible);

  const drawerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    const height = node.getBoundingClientRect().height;
    document.body.style.setProperty(
      "--departure-flight-drawer-height",
      `${height}px`,
    );
  }, []);

  useEffect(() => {
    return () => {
      document.body.style.setProperty("--departure-flight-drawer-height", "0");
    };
  }, []);

  if (!selectedDepartureFlight) return null;

  const firstSegment = selectedDepartureFlight.segments[0];
  const lastSegment =
    selectedDepartureFlight.segments[
      selectedDepartureFlight.segments.length - 1
    ];

  const includedBnplFee = selectedOffer
    ? includeBnplFee(selectedOffer?.totalPrice)
    : null;
  const monthlyPayment = includedBnplFee
    ? calculateMonthlyInstallment(includedBnplFee, maxAvailableInstallments)
    : null;

  return (
    <div
      className="absolute bottom-0 z-20 flex w-full flex-col gap-2 rounded-t-4xl border-1 border-stroke-neutral-soft bg-surface-base-light p-4"
      style={{
        boxShadow:
          "0px -4px 12px 0px rgba(0, 0, 0, 0.04), 0px -1px 12px 0px rgba(0, 0, 0, 0.08)",
      }}
      ref={drawerRef}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex flex-col items-start gap-1">
          <div className="flex justify-center gap-1">
            <div className="relative inline-flex">
              <div className="absolute inset-1 rounded-full bg-surface-accent-soft" />
              <CheckCircleIcon
                size={24}
                className="relative text-surface-accent-stronger"
              />
            </div>
            <span>{t("flights.departureFlight")}</span>
          </div>
          <div className="flex items-center gap-1 ps-7 text-sm font-medium text-text-tertiary">
            {firstSegment.departure.city.code}
            <ArrowRightIcon size={12} />
            {lastSegment.arrival.city.code}
            <span className="text-stroke-neutral-medium">&bull;</span>
            {firstSegment.departure.time}
            {" - "}
            {lastSegment.arrival.time}
          </div>
        </div>
        <Button
          variant="text"
          size="small"
          sx={{ mx: "-16px" }}
          onClick={() => {
            history.back();
          }}
        >
          {t("change")}
        </Button>
      </div>
      <Divider sx={{ borderColor: "var(--color-stroke-neutral-softer)" }} />
      {selectedOffer ? (
        <div className="flex flex-col gap-2 py-2">
          <div className="flex justify-between">
            <span className="text-sm text-text-primary">
              {t("payment.totalRoundTripPrice")}
            </span>
            <span className="font-bold">
              {formatCurrency(selectedOffer.totalPrice, selectedOffer.currency)}
            </span>
          </div>
          {isBnplFeatureEnabled && monthlyPayment ? (
            <div className="flex justify-between">
              <span className="text-sm text-text-primary">
                {t("payment.payMonthly")}
              </span>
              <span className="font-bold">
                {formatCurrency(monthlyPayment, selectedOffer.currency)}
              </span>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
